import { type FormEvent, useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

/** The API's cap, the same as the capturing unit of an encounter. */
export const PROFILE_UNIT_MAX_LENGTH = 120

export interface ProfileUnitFieldProps {
  /** The saved unit; null when none is set. */
  value: string | null
  saving: boolean
  /** The trimmed unit, or null to clear it. Called only for a change. */
  onSave: (unit: string | null) => void
}

/** What a typed value saves as: trimmed, and blank means no unit. */
export const normaliseUnit = (typed: string): string | null => typed.trim() || null

/**
 * The operator's unit ("Útvar", ADR-0051): it prefills the capturing unit of every HUMINT
 * encounter they record. The caller shows it only while HUMINT is on, and remounts it (a `key`
 * on the saved value) when a save lands, so the draft starts from what was saved.
 */
export function ProfileUnitField({ value, saving, onSave }: ProfileUnitFieldProps) {
  const { t } = useTranslation()
  const id = useId()
  const hintId = `${id}-hint`
  const [draft, setDraft] = useState(value ?? '')
  const next = normaliseUnit(draft)
  const unchanged = next === value
  const submit = (event: FormEvent) => {
    event.preventDefault()
    if (!unchanged && !saving) onSave(next)
  }
  return (
    <form onSubmit={submit} className="flex flex-col gap-1.5 sm:col-span-2">
      <Label htmlFor={id} className="text-muted-foreground text-xs font-normal">
        {t('settings.profile.unit')}
      </Label>
      <div className="flex items-center gap-2">
        <Input
          id={id}
          value={draft}
          maxLength={PROFILE_UNIT_MAX_LENGTH}
          placeholder={t('settings.profile.unitPlaceholder')}
          aria-describedby={hintId}
          onChange={event => setDraft(event.target.value)}
        />
        <Button
          type="submit"
          size="sm"
          variant="outline"
          className="max-md:h-9"
          disabled={unchanged || saving}
        >
          {saving ? t('settings.profile.unitSaving') : t('common.save')}
        </Button>
      </div>
      <p id={hintId} className="text-xs text-muted-foreground">
        {t('settings.profile.unitHint')}
      </p>
    </form>
  )
}
